import Swiper from "swiper";
import { Navigation, Pagination } from "swiper/modules";

const youtubeHasPlayed = new WeakMap();
const youtubeIframes = new WeakMap();
const youtubeLastTime = new WeakMap();
const seekingVideos = new WeakSet();

const getVideoId = url => url?.match(/embed\/([^?&/]+)/)?.[1] || "";
const getIframe = wrapper => youtubeIframes.get(wrapper) || wrapper?.querySelector("iframe");
const postYouTube = (iframe, func, args = []) => {
    iframe?.contentWindow?.postMessage(JSON.stringify({ event: "command", func, args }), "*");
};

const setYouTubePlaying = (wrapper, playing) => {
    const span = wrapper.querySelector(".pul-video-banner__video-overlay span");
    playing ? span?.setAttribute("hidden", "") : span?.removeAttribute("hidden");
    wrapper.classList.toggle("playing", playing);
};

const pauseYouTubeVideo = wrapper => {
    postYouTube(getIframe(wrapper), "pauseVideo");
    setYouTubePlaying(wrapper, false);
};

const handleYouTubeState = (wrapper, state) => {
    if (state === 1) {
        youtubeHasPlayed.set(wrapper, true);
        return setYouTubePlaying(wrapper, true);
    }
    if (state === 3 || (!youtubeHasPlayed.get(wrapper) && !wrapper.classList.contains("playing"))) {
        return;
    }
    if (state === 2 || state === -1) {
        return setYouTubePlaying(wrapper, false);
    }
    if (state === 0) {
        const iframe = getIframe(wrapper);
        youtubeHasPlayed.set(wrapper, false);
        postYouTube(iframe, "seekTo", [0, true]);
        postYouTube(iframe, "pauseVideo");
        setYouTubePlaying(wrapper, false);
    }
};

const handleYouTubeInfo = (wrapper, info) => {
    if (typeof info?.playerState === "number") {
        handleYouTubeState(wrapper, info.playerState);
        if (typeof info.currentTime === "number") {
            youtubeLastTime.set(wrapper, info.currentTime);
        }
        return;
    }

    // Plain embeds often omit playerState and only stream currentTime while playing.
    if (wrapper.querySelector(".pul-video-banner__video-overlay") || typeof info?.currentTime !== "number") {
        return;
    }

    const lastTime = youtubeLastTime.get(wrapper);
    const advancing = typeof lastTime === "number" && info.currentTime > lastTime + 0.05;
    youtubeLastTime.set(wrapper, info.currentTime);

    if (advancing && info.playbackRate > 0) {
        youtubeHasPlayed.set(wrapper, true);
        setYouTubePlaying(wrapper, true);
    }
};

const parseYouTubePayload = data => {
    if (typeof data === "string") {
        try {
            data = JSON.parse(data);
        } catch {
            return null;
        }
    }
    if (data?.event === "onStateChange" && typeof data.info === "number") {
        return { playerState: data.info };
    }
    return data?.info && typeof data.info === "object" ? data.info : null;
};

const findYouTubeWrapper = source => {
    for (const iframe of document.querySelectorAll(".pul-video-banner__video iframe")) {
        if (iframe.contentWindow === source) {
            const wrapper = iframe.closest(".pul-video-banner__video");
            youtubeIframes.set(wrapper, iframe);
            return wrapper;
        }
    }
    return null;
};

let youtubeMessagesBound = false;
const bindYouTubeMessages = () => {
    if (youtubeMessagesBound) {
        return;
    }
    youtubeMessagesBound = true;
    window.addEventListener("message", ({ origin, data, source }) => {
        try {
            if (!/(?:^|\.)youtube(?:-nocookie)?\.com$/.test(new URL(origin).hostname)) {
                return;
            }
        } catch {
            return;
        }
        const info = parseYouTubePayload(data);
        const wrapper = info && findYouTubeWrapper(source);
        if (wrapper) {
            handleYouTubeInfo(wrapper, info);
        }
    });
};

const enableYouTubeApi = iframe => {
    try {
        const url = new URL(iframe.src, window.location.href);
        if (url.searchParams.get("enablejsapi") === "1") {
            return;
        }
        url.searchParams.set("enablejsapi", "1");
        url.searchParams.set("origin", window.location.origin);
        iframe.src = url.toString();
    } catch { /* invalid src */ }
};

const listenToYouTubeIframe = (wrapper, iframe) => {
    bindYouTubeMessages();
    youtubeIframes.set(wrapper, iframe);
    const startListening = () => {
        iframe.contentWindow?.postMessage(JSON.stringify({
            event: "listening",
            id: getVideoId(iframe.src),
            channel: "widget",
        }), "*");
    };
    iframe.addEventListener("load", startListening);
    startListening();
};

const playYouTubeVideo = wrapper => {
    const iframe = getIframe(wrapper);
    if (!iframe || wrapper.classList.contains("playing")) {
        return;
    }
    postYouTube(iframe, "playVideo");
    setYouTubePlaying(wrapper, true);
};

const bindYouTube = wrapper => {
    const iframe = wrapper.querySelector("iframe[src*='youtube']");
    if (!iframe || youtubeIframes.get(wrapper) === iframe) {
        return;
    }
    enableYouTubeApi(iframe);
    listenToYouTubeIframe(wrapper, iframe);
};

const bindAllYouTube = () => {
    document.querySelectorAll(".pul-video-banner__video").forEach(bindYouTube);
};

const setNativeVideoPlaying = (wrapper, video, button, playing) => {
    video.classList.toggle("playing", playing);
    wrapper.classList.toggle("playing", playing);
    button.classList.toggle("playing", playing);
    playing ? video.setAttribute("controls", "") : video.removeAttribute("controls");
};

const bindNativeVideo = (wrapper, video, button) => {
    button.addEventListener("click", () => {
        if (video.paused) {
            setNativeVideoPlaying(wrapper, video, button, true);
            video.play();
        }
    });
    video.addEventListener("seeking", () => seekingVideos.add(video));
    video.addEventListener("seeked", () => seekingVideos.delete(video));
    video.addEventListener("ended", () => setNativeVideoPlaying(wrapper, video, button, false));
    video.addEventListener("pause", () => {
        if (!seekingVideos.has(video)) {
            setNativeVideoPlaying(wrapper, video, button, false);
        }
    });
};

const pauseSlideMedia = slide => {
    const wrapper = slide?.querySelector(".pul-video-banner__video");
    const video = slide?.querySelector("video");
    const button = slide?.querySelector(".pul-video-banner__video-overlay");

    if (video) {
        video.pause();
        setNativeVideoPlaying(wrapper, video, button, false);
    } else if (wrapper?.querySelector("iframe[src*='youtube']")) {
        pauseYouTubeVideo(wrapper);
    }
};

export function handleVideoBanner() {
    document.querySelectorAll(".pul-video-banner:not(.pul-video-banner--autoplay-video) .pul-video-banner__video")
        .forEach(wrapper => {
            const button = wrapper.querySelector(".pul-video-banner__video-overlay");
            const video = wrapper.querySelector("video");
            const iframe = wrapper.querySelector("iframe[src*='youtube']");

            if (iframe && button) {
                bindYouTube(wrapper);
                button.addEventListener("click", () => playYouTubeVideo(wrapper));
            } else if (video && button) {
                bindNativeVideo(wrapper, video, button);
            } else if (iframe) {
                bindYouTube(wrapper);
            }
        });

    bindAllYouTube();
    ["OneTrustGroupsUpdated", "consent.onetrust", "OtPopupClose"]
        .forEach(eventName => window.addEventListener(eventName, bindAllYouTube));

    const observer = new MutationObserver(bindAllYouTube);
    document.querySelectorAll(".pul-video-banner").forEach(banner => {
        observer.observe(banner, { childList: true, subtree: true, attributes: true, attributeFilter: ["src"] });
    });
}

export function handleAutoplayingVideoBanner() {
    document.querySelectorAll(".pul-video-banner--autoplay-video video").forEach(video => {
        new IntersectionObserver(([entry]) => {
            entry.intersectionRatio >= 0.5 ? video.play() : video.pause();
        }, { threshold: [0, 0.25, 0.5, 0.75, 1] }).observe(video);
    });
}

export function initVideoBannerSlider() {
    document.querySelectorAll(".pul-video-banner .swiper").forEach(slider => {
        const slideCount = slider.querySelectorAll(".swiper-wrapper > .swiper-slide").length;

        new Swiper(slider, {
            modules: [Navigation, Pagination],
            spaceBetween: 16,
            ...(slideCount > 2 ? { loop: true } : { rewind: true }),
            pagination: { el: slider.querySelector(".swiper-pagination"), clickable: true },
            navigation: {
                nextEl: slider.querySelector(".swiper-button-next"),
                prevEl: slider.querySelector(".swiper-button-prev"),
            },
            on: {
                slideChangeTransitionStart: ({ el }) => {
                    el.querySelectorAll(".swiper-slide:not(.swiper-slide-active)").forEach(pauseSlideMedia);
                },
            },
        });
    });
}
